import * as React from 'react'
import { DefaultTheme } from '@mui/system'
import { styled, createTheme, ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import Box from '@mui/material/Box'
import AppBar from '@mui/material/AppBar'
import Typography from '@mui/material/Typography'
import DataContextProvider from '../contexts/DataProvider'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import DateAdapter from '@mui/lab/AdapterDayjs'
import { Container, IconButton } from '@mui/material'
import LogoutIcon from '@mui/icons-material/Logout'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import { isBrowser, useAuth0 } from '../services/auth'
import { Link } from 'gatsby'

interface ThemeProviderProps<Theme = DefaultTheme> {
  children?: React.ReactNode
  theme: Partial<Theme> | ((outerTheme: Theme) => Theme)
}

type Props = {
  children: JSX.Element
  uri: string
}

const mdTheme = createTheme({
  palette: {
    primary: {
      main: '#9ae9d1',
      contrastText: '#2c3f3b',
    },
    secondary: {
      main: '#415f58',
    },
  },
})
const headerHeight = '3.75rem'

const StyledAppBar = styled(AppBar)(() => ({
  height: headerHeight,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  padding: '0 4.6rem',
  '& .MuiContainer-root': {
    display: 'flex',
    justifyContent: 'space-between',
  },
}))

const StyledLogo = styled(Link)(({ theme }) => ({
  textDecoration: 'none',
  color: theme.palette.secondary.main,
}))

const StyledUser = styled('div')(() => ({
  span: {
    paddingRight: '1.25rem',
  },
}))

const StyledBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900],
  flexGrow: 1,
  overflow: 'auto',
  padding: '0 2rem 4rem',
  height: `calc(100vh - ${headerHeight})`,
}))

export default function Layout({ children }: Props): React.ReactElement<ThemeProviderProps<DefaultTheme>> {
  const { isAuthenticated, user, logout } = useAuth0()
  const title = 'MS sherpa Internal Dashboard'
  return (
    <ThemeProvider theme={mdTheme}>
      <LocalizationProvider dateAdapter={DateAdapter}>
        <DataContextProvider>
          <Box>
            <CssBaseline />

            <StyledAppBar position="static">
              <Container maxWidth="lg">
                <StyledLogo to="/">
                  <Typography component="h1" variant="h6" color="inherit" noWrap>
                    {title}
                  </Typography>
                </StyledLogo>
                {isAuthenticated && user && (
                  <StyledUser>
                    <span>{user.name}</span>
                    <Link to="/profile/">
                      <IconButton>
                        <AccountCircleIcon color="secondary" />
                      </IconButton>
                    </Link>
                    <IconButton onClick={() => logout({ returnTo: isBrowser() ? window.location.origin : '/' })}>
                      <LogoutIcon color="secondary" />
                    </IconButton>
                  </StyledUser>
                )}
              </Container>
            </StyledAppBar>

            <StyledBox component="main" id="main-wrapper">
              {children}
            </StyledBox>
          </Box>
        </DataContextProvider>
      </LocalizationProvider>
    </ThemeProvider>
  )
}
